import { Check, Clock, Copy, Mail } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/common/ui/button";
import { Checkbox } from "@/common/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/common/ui/dialog";
import { Input } from "@/common/ui/input";
import { Label } from "@/common/ui/label";
import { cn } from "@/lib/utils";

import { acceptInvitationUrl } from "../accept-invitation-route";
import { createInvitation } from "../api/organization-api";
import { formatInvitationExpiry } from "../invitation-expiry";
import type { SessionMembershipRole } from "../types";

interface InviteMemberModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgUnitId: string;
  orgUnitName: string;
  availableRoles: SessionMembershipRole[];
  onInviteSent: () => void;
}

export function InviteMemberModal({
  open,
  onOpenChange,
  orgUnitId,
  orgUnitName,
  availableRoles,
  onInviteSent,
}: InviteMemberModalProps): React.ReactElement {
  const [email, setEmail] = useState("");
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [invitationLink, setInvitationLink] = useState("");
  const [invitationExpiresAt, setInvitationExpiresAt] = useState("");
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setEmail("");
      setSelectedRoleIds([]);
      setSuccess(false);
      setInvitationLink("");
      setInvitationExpiresAt("");
      setCopied(false);
      setError("");
    }
  }, [open]);

  const validateEmail = (value: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };

  const handleSubmit = async (): Promise<void> => {
    setError("");

    if (!email.trim()) {
      setError("Email is required");
      return;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    const validRoleIds = selectedRoleIds.filter(Boolean);
    if (validRoleIds.length === 0) {
      setError("Please select at least one role");
      return;
    }

    setIsSubmitting(true);

    try {
      const invitation = await createInvitation(orgUnitId, {
        email,
        role_ids: validRoleIds,
      });

      setInvitationLink(acceptInvitationUrl(invitation.token));
      setInvitationExpiresAt(invitation.expires_at);
      setSuccess(true);
      onInviteSent();
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      if (message.includes("Invalid") || message.includes("Validation")) {
        setError("Please check the email and role selections and try again.");
      } else if (message.includes("already") || message.includes("duplicate")) {
        setError("An invitation has already been sent to this email address.");
      } else if (message.includes("Forbidden") || message.includes("permission")) {
        setError("You don't have permission to invite members to this organization.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(invitationLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable (non-secure context or denied)
    }
  };

  const handleClose = (): void => {
    onOpenChange(false);
  };

  const toggleRole = (roleId: string): void => {
    setSelectedRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
    );
  };

  if (success) {
    return (
      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) onOpenChange(false);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/10">
              <Check className="h-6 w-6 text-emerald-500" />
            </div>
            <DialogTitle className="text-center">Invitation created</DialogTitle>
            <DialogDescription className="text-center">
              Share this link with <strong>{email}</strong>. They&apos;ll need to sign in with that
              address to accept.
            </DialogDescription>
          </DialogHeader>

          {invitationLink && (
            <div className="space-y-3">
              <Label htmlFor="invitation-link" className="text-sm text-muted-foreground">
                Invitation link
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="invitation-link"
                  readOnly
                  value={invitationLink}
                  className="bg-muted text-sm"
                />
                <Button
                  variant="outline"
                  size="icon"
                  className="h-11 w-11 shrink-0"
                  aria-label="Copy invitation link"
                  onClick={handleCopy}
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-emerald-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="h-3.5 w-3.5" />
                {formatInvitationExpiry(invitationExpiresAt)}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={handleClose} className="w-full">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onOpenChange(false);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite Member</DialogTitle>
          <DialogDescription>
            Send an invitation to join <strong>{orgUnitName}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="invite-email">Email address</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="invite-email"
                type="email"
                placeholder="colleague@company.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError("");
                }}
                className={cn("pl-10", error && "border-destructive")}
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          <div className="space-y-3">
            <Label>Roles</Label>
            {availableRoles.map((role) => (
              <label
                key={role.role_id}
                htmlFor={`role-${role.role_id}`}
                className={cn(
                  "flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors",
                  selectedRoleIds.includes(role.role_id)
                    ? "border-primary bg-primary/5"
                    : "hover:bg-accent"
                )}
              >
                <Checkbox
                  id={`role-${role.role_id}`}
                  checked={selectedRoleIds.includes(role.role_id)}
                  onCheckedChange={() => toggleRole(role.role_id)}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium">{role.role_name}</p>
                  <p className="text-xs text-muted-foreground">{role.role_key}</p>
                </div>
              </label>
            ))}
          </div>

          <div className="flex items-center gap-2 rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>Invitation expires in 7 days</span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? "Sending..." : "Send Invitation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
